import { apiRequest, type Schemas } from "./client";

export type CustomerSession = Schemas["CustomerSession"];
export type Address = Schemas["Address"];
export type AddressFields = Omit<Address, "id" | "postal_code" | "urgent_delivery"> & { postal_code: string };

const data = <T>(response: { data: T }) => response.data;

/** Where a code goes: a phone number (the 10 national digits are fine) or an email. */
export type Destination = { phone_number: string } | { email_address: string };
export type SignUpCodeSent = Schemas["SignUpCodeSent"];
export type SocialProvider = "google" | "facebook";

/**
 * Customer sign-in and registering (design doc §6.1). Registering by phone or email sends a
 * code, the code earns a sign-up token, and the token and a password make the account.
 * A forgotten password is the same code, then a new password.
 */
export const sessionApi = {
  get: () => apiRequest<CustomerSession>("/api/v1/session"),
  signIn: (login: string, password: string) => apiRequest<CustomerSession>("/api/v1/session", { method: "POST", body: { login, password } }),
  redeemLink: (token: string) => apiRequest<CustomerSession>("/api/v1/session", { method: "POST", body: { token } }),
  signOut: () => apiRequest<void>("/api/v1/session", { method: "DELETE" }),
  sendSignUpCode: (destination: Destination) => apiRequest<SignUpCodeSent>("/api/v1/sign_up_codes", { method: "POST", body: destination }),
  verifySignUpCode: (destination: Destination, code: string) =>
    apiRequest<{ sign_up_token: string }>("/api/v1/sign_up_codes/verify", { method: "POST", body: { ...destination, code } }),
  register: (sign_up_token: string, password: string) => apiRequest<CustomerSession>("/api/v1/users", { method: "POST", body: { sign_up_token, password } }),
  socialSignIn: (provider: SocialProvider, credential: string) =>
    apiRequest<CustomerSession>("/api/v1/social_sessions", { method: "POST", body: { provider, credential } }),
  emailLink: (email_address: string) => apiRequest<void>("/api/v1/sign_in_links", { method: "POST", body: { email_address } }),
  sendResetCode: (destination: Destination) => apiRequest<void>("/api/v1/password_resets", { method: "POST", body: destination }),
  resetPassword: (destination: Destination, code: string, password: string) =>
    apiRequest<CustomerSession>("/api/v1/password_resets", { method: "PATCH", body: { ...destination, code, password } }),
  resendConfirmation: () => apiRequest<void>("/api/v1/email_confirmations", { method: "POST" }),
  confirmEmail: (token: string) => apiRequest<void>("/api/v1/email_confirmations", { method: "PATCH", body: { token } }),
};

/** The signed-in customer's own account (PRD screen 17). */
export const accountApi = {
  changePassword: (current_password: string, password: string) =>
    apiRequest<Schemas["UserRecord"]>("/api/v1/account", { method: "PATCH", body: { current_password, password } }).then(data),
  addresses: () => apiRequest<Schemas["AddressList"]>("/api/v1/addresses").then(data),
  createAddress: (address: AddressFields) =>
    apiRequest<Schemas["AddressRecord"]>("/api/v1/addresses", { method: "POST", body: { address } }).then(data),
  updateAddress: (id: number, address: AddressFields) =>
    apiRequest<Schemas["AddressRecord"]>(`/api/v1/addresses/${id}`, { method: "PATCH", body: { address } }).then(data),
  deleteAddress: (id: number) => apiRequest<void>(`/api/v1/addresses/${id}`, { method: "DELETE" }),
};
